import React, { useState, useEffect } from 'react';
import { Box, AppBar, Toolbar, Typography, InputBase, IconButton, Avatar, Badge, Menu, MenuItem, Divider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import { colors, layout } from '../theme/designSystem';
import AlertDrawer from './AlertDrawer';
import { alertService } from '../services/api';
import { useWebSocket } from '../context/WebSocketContext';
import { useAuth } from '../context/AuthContext';

interface TopBarProps {
  title?: string;
  onMenuClick?: () => void;
  isMobile?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ title = 'Dashboard', onMenuClick, isMobile = false }) => {
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { latestAlert } = useWebSocket();
  const { user, logout } = useAuth();

  useEffect(() => {
    loadAlertStats();
  }, []);

  useEffect(() => {
    if (latestAlert) {
      // New alert received via WebSocket
      loadAlertStats();
    }
  }, [latestAlert]);

  const loadAlertStats = async () => {
    try {
      const stats = await alertService.getStats();
      setUnacknowledgedCount(stats.unacknowledged || 0);
    } catch (error) {
      console.error('Failed to load alert stats:', error);
    }
  };

  const handleAlertIconClick = () => {
    setAlertDrawerOpen(true);
  };

  const handleAlertDrawerClose = () => {
    setAlertDrawerOpen(false);
    loadAlertStats();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    return user.username.substring(0, 2).toUpperCase();
  };

  const getUserRole = () => {
    if (!user?.roles || user.roles.length === 0) return 'User';
    const role = user.roles[0];
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <>
      <AppBar
      position="fixed"
      sx={{
        left: { xs: 0, md: layout.sidebar.width },
        width: { xs: '100%', md: `calc(100% - ${layout.sidebar.width})` },
        height: layout.topBar.height,
        backgroundColor: layout.topBar.background,
        borderBottom: `1px solid ${colors.neutral[200]}`,
      }}
    >
      <Toolbar
        sx={{
          height: '100%',
          px: { xs: 2, sm: 3, md: 4 },
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: layout.topBar.height,
        }}
      >
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2, color: colors.neutral[900] }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Page Title */}
        <Typography
          variant="h4"
          sx={{
            color: colors.neutral[900],
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* Search Bar - Hidden on mobile */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: '0px', sm: '200px', md: '320px' },
              height: '40px',
              backgroundColor: colors.neutral[100],
              borderRadius: '10px',
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              transition: 'all 0.3s ease',
              '&:focus-within': {
                backgroundColor: '#ffffff',
                border: `2px solid ${colors.primary[600]}`,
              },
            }}
          >
            <SearchIcon
              sx={{
                position: 'absolute',
                left: '12px',
                color: colors.neutral[400],
                fontSize: '20px',
              }}
            />
            <InputBase
              placeholder="Search..."
              sx={{
                width: '100%',
                pl: '40px',
                pr: 2,
                fontSize: '0.875rem',
                color: colors.neutral[900],
              }}
            />
          </Box>

          {/* Notifications */}
          <IconButton
            onClick={handleAlertIconClick}
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: colors.neutral[100],
              },
            }}
          >
            <Badge badgeContent={unacknowledgedCount} color="error">
              <NotificationsIcon sx={{ fontSize: '20px' }} />
            </Badge>
          </IconButton>

          {/* User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.neutral[900] }}>
                {user?.username || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
                {getUserRole()}
              </Typography>
            </Box>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: colors.status.success,
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  border: '2px solid #ffffff',
                },
              }}
            >
              <IconButton
                onClick={handleMenuOpen}
                sx={{ p: 0 }}
              >
                <Avatar
                  sx={{
                    width: '36px',
                    height: '36px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    cursor: 'pointer',
                  }}
                >
                  {getUserInitials()}
                </Avatar>
              </IconButton>
            </Badge>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
    
    {/* User Menu */}
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 200,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <MenuItem disabled>
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {user?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleMenuClose}>
        <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
        Profile
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
        Logout
      </MenuItem>
    </Menu>
    
    <AlertDrawer
      open={alertDrawerOpen}
      onClose={handleAlertDrawerClose}
      onAlertAcknowledged={loadAlertStats}
    />
    </>
  );
};

export default TopBar;
